import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Music,
  Utensils,
  Camera,
  Flame,
  Lightbulb,
  MapPin,
  Mic2,
  Phone,
  FileText,
  PartyPopper,
  Star,
  CheckCircle2,
  ChevronDown,
} from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/contact-sax-dj/30min';
const PAGE_URL    = 'https://sax-dj.fr/soirees-entreprise';

const PAGE_TITLE = "Soirée d'entreprise clé en main | Sax&DJ Rambouillet";
const PAGE_DESC  = "Soirée d'entreprise clé en main à Rambouillet et Chartres. Salle, traiteur, saxophone live, DJ, animations. 1 interlocuteur, devis sur-mesure. Yvelines (78), Île-de-France.";

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://sax-dj.fr/#organization",
      "name": "Sax&DJ",
      "url": "https://sax-dj.fr",
      "logo": "https://sax-dj.fr/saxDJLOGO.png",
      "telephone": "+33661944226",
      "email": "contact@sax-dj.fr",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "Île-de-France",
        "addressCountry": "FR"
      },
      "areaServed": ["Rambouillet", "Chartres", "Yvelines", "Eure-et-Loir", "Île-de-France"]
    },
    {
      "@type": "Service",
      "@id": `${PAGE_URL}#service`,
      "name": "Organisation de soirées d'entreprise clé en main",
      "description": "Sax&DJ prend en charge l'intégralité de votre soirée d'entreprise : salle, traiteur, saxophone live, DJ, photographe, animations feu, lumières. Un seul interlocuteur, devis sur-mesure.",
      "provider": { "@id": "https://sax-dj.fr/#organization" },
      "areaServed": ["Rambouillet", "Chartres", "Yvelines", "Île-de-France Sud"],
      "serviceType": "Organisation événementielle",
      "url": PAGE_URL
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${PAGE_URL}#breadcrumb`,
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil",               "item": "https://sax-dj.fr/" },
        { "@type": "ListItem", "position": 2, "name": "Soirées d'entreprise",  "item": PAGE_URL }
      ]
    },
    {
      "@type": "FAQPage",
      "@id": `${PAGE_URL}#faq`,
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Quelle zone géographique couvrez-vous pour les soirées d'entreprise ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sax&DJ intervient en région Rambouillet, Chartres, Yvelines (78) et Eure-et-Loir (28). L'Île-de-France Sud est entièrement couverte. Une intervention hors zone reste possible selon le projet."
          }
        },
        {
          "@type": "Question",
          "name": "Comment obtenir un devis pour une soirée d'entreprise ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Réservez un RDV via Calendly ou envoyez une demande via le formulaire. On échange sur votre projet : date, lieu, nombre de personnes, ambiance. Sax&DJ vous envoie ensuite un devis complet, tous prestataires inclus."
          }
        },
        {
          "@type": "Question",
          "name": "Quels prestataires Sax&DJ peut-il coordonner pour ma soirée ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sax&DJ peut coordonner jusqu'à 8 types de prestataires : salle/lieu de réception, traiteur, saxophone live, DJ, photographe, animation feu, éclairages et scène, animations sur-mesure. La sélection est entièrement adaptée à votre budget et vos envies."
          }
        },
        {
          "@type": "Question",
          "name": "Peut-on avoir le saxophone live ET un DJ pour la même soirée d'entreprise ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Oui, c'est même la formule la plus appréciée. Le saxophone assure l'ambiance du cocktail ou de l'accueil, et le DJ prend le relais pour la soirée dansante. Les deux prestations sont coordonnées et facturées dans un seul devis."
          }
        }
      ]
    }
  ]
};

const inclusions = [
  { icon: MapPin,      label: 'Lieu / Salle' },
  { icon: Music,       label: 'DJ' },
  { icon: Mic2,        label: 'Saxophone live' },
  { icon: Utensils,    label: 'Traiteur' },
  { icon: Camera,      label: 'Photographe' },
  { icon: Flame,       label: 'Animation feu' },
  { icon: Lightbulb,   label: 'Lumières & scène' },
  { icon: PartyPopper, label: 'Animations sur-mesure' },
];

const stats = [
  { number: '8',     label: 'types de prestataires coordonnés' },
  { number: '1',     label: 'seul interlocuteur & devis' },
  { number: '100%',  label: 'sur-mesure, zéro pack fixe' },
  { number: '78/28', label: 'Yvelines & Eure-et-Loir' },
];

const steps = [
  {
    icon: Phone,
    title: 'RDV téléphonique',
    description: 'On échange sur votre projet : date, lieu, nombre de personnes, ambiance souhaitée. Aucun engagement.',
  },
  {
    icon: FileText,
    title: 'Devis sur-mesure',
    description: "Un devis unique, clair et détaillé regroupant tous les prestataires. Vous n'avez qu'un seul interlocuteur.",
  },
  {
    icon: PartyPopper,
    title: 'On gère tout, vous profitez',
    description: "Sax&DJ coordonne chaque prestataire de A à Z. Le jour J, le saxophone est sur scène et l'ambiance est entre de bonnes mains.",
  },
];

const differentiators = [
  {
    title: 'Artiste ET organisateur',
    description: "Sax&DJ ne sous-traite pas l'ambiance. Le saxophone est sur scène le jour J, l'ambiance avec.",
  },
  {
    title: 'Un seul devis, zéro stress',
    description: 'Tous les prestataires regroupés dans une seule facture. Vous validez une fois, on s\'occupe du reste.',
  },
  {
    title: 'Réseau local de confiance',
    description: 'Prestataires sélectionnés et éprouvés en région Rambouillet / Chartres / Île-de-France Sud.',
  },
  {
    title: 'Devis sur-mesure',
    description: 'Pas de packs fixes. Chaque soirée est construite selon vos besoins et votre budget.',
  },
];

const faqs = [
  {
    question: "Quelle zone géographique couvrez-vous pour les soirées d'entreprise ?",
    answer: "Sax&DJ intervient en région Rambouillet, Chartres, Yvelines (78) et Eure-et-Loir (28). L'Île-de-France Sud est entièrement couverte. Une intervention hors zone reste possible selon le projet.",
  },
  {
    question: "Comment obtenir un devis pour une soirée d'entreprise ?",
    answer: "Réservez un RDV via Calendly ou envoyez une demande via le formulaire. On échange sur votre projet : date, lieu, nombre de personnes, ambiance. Vous recevez ensuite un devis complet, tous prestataires inclus.",
  },
  {
    question: "Quels prestataires Sax&DJ peut-il coordonner pour ma soirée ?",
    answer: "Jusqu'à 8 types de prestataires : salle/lieu de réception, traiteur, saxophone live, DJ, photographe, animation feu, éclairages & scène, animations sur-mesure. La sélection est entièrement adaptée à votre budget et vos envies.",
  },
  {
    question: "Peut-on avoir le saxophone live ET un DJ pour la même soirée ?",
    answer: "Oui, c'est même la formule la plus appréciée. Le saxophone assure l'ambiance du cocktail ou de l'accueil, et le DJ prend le relais pour la soirée dansante. Les deux prestations sont coordonnées et facturées dans un seul devis.",
  },
];

export const SoireesEntreprise = () => {

  /* ── SEO / GEO : injection dynamique des métadonnées ── */
  useEffect(() => {
    // Titre
    document.title = PAGE_TITLE;

    const setMeta = (attr, name, content) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
      return el;
    };

    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
      el.setAttribute('href', href);
      return el;
    };

    // Meta description
    setMeta('name', 'description', PAGE_DESC);
    // Canonical
    setLink('canonical', PAGE_URL);
    // Open Graph
    setMeta('property', 'og:title',       PAGE_TITLE);
    setMeta('property', 'og:description', PAGE_DESC);
    setMeta('property', 'og:url',         PAGE_URL);
    setMeta('property', 'og:type',        'website');
    setMeta('property', 'og:image',       'https://sax-dj.fr/saxDJLOGO.png');
    setMeta('property', 'og:locale',      'fr_FR');
    // Twitter Card
    setMeta('name', 'twitter:card',        'summary_large_image');
    setMeta('name', 'twitter:title',       PAGE_TITLE);
    setMeta('name', 'twitter:description', PAGE_DESC);
    setMeta('name', 'twitter:image',       'https://sax-dj.fr/saxDJLOGO.png');

    // JSON-LD
    let script = document.getElementById('jsonld-soirees-entreprise');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id   = 'jsonld-soirees-entreprise';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(JSON_LD);

    return () => {
      document.title = "Sax&DJ | Saxophone & DJ — Mariages et Événements en Île-de-France";
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
      const jsonld = document.getElementById('jsonld-soirees-entreprise');
      if (jsonld) jsonld.remove();
    };
  }, []);

  return (
    <div className="min-h-screen">

      {/* ── HERO ── */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 py-24 md:py-36 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gold font-semibold uppercase tracking-widest text-sm mb-4">
            Rambouillet · Chartres · Île-de-France Sud
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Soirée d'entreprise clé en main
          </h1>
          <p className="text-xl md:text-2xl text-white/85 mb-10 leading-relaxed">
            Un seul contact · Un seul devis · Zéro stress
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              asChild
              className="bg-gold hover:bg-yellow-500 text-blue-900 font-bold text-lg px-8 py-4 hover:shadow-xl transition-all duration-300 border-0"
            >
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                Prendre un rendez-vous
              </a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-4 transition-all duration-300"
            >
              <Link to="/contact">
                Envoyer une demande
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="bg-white border-b border-blue-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="text-3xl font-bold text-blue-900 mb-1">{s.number}</div>
                <div className="text-sm text-warm-gray">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ACCROCHE ── */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Star className="h-10 w-10 text-gold mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-6">
            Vous méritez une soirée dont on parle encore
          </h2>
          <p className="text-lg text-warm-gray leading-relaxed mb-6">
            Organiser une <strong className="text-charcoal">soirée d'entreprise</strong> réussie prend du temps, des contacts et de l'expérience.
            Sax&DJ, basé en région <strong className="text-charcoal">Rambouillet</strong>, prend en charge toute la logistique.
            Salle, traiteur, saxophone live, animations, lumières : un seul devis, un seul interlocuteur.
          </p>
          <ul className="text-left inline-block space-y-2 text-warm-gray text-sm mb-4">
            <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-900 flex-shrink-0" /> Cocktails, séminaires, soirées de fin d'année, lancements de produits</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-900 flex-shrink-0" /> De quelques dizaines à plusieurs centaines de personnes</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-900 flex-shrink-0" /> Budget variable, devis 100% sur-mesure, aucun pack imposé</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-900 flex-shrink-0" /> Zone couverte : Rambouillet, Chartres, Yvelines (78), Île-de-France Sud</li>
          </ul>
        </div>
      </section>

      {/* ── DIFFÉRENCIATEURS ── */}
      <section className="py-20 bg-gradient-to-br from-ivory/30 to-pearl/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Pourquoi Sax&DJ ?
            </h2>
            <p className="text-lg text-warm-gray max-w-2xl mx-auto">
              Saxophone live, DJ et coordination complète : un interlocuteur unique pour une soirée qui sort de l'ordinaire.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300 flex items-start space-x-4"
              >
                <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-2 rounded-lg flex-shrink-0 mt-0.5">
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-charcoal mb-1">{item.title}</h3>
                  <p className="text-warm-gray text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CE QU'ON PEUT INCLURE ── */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Ce qu'on peut inclure
            </h2>
            <p className="text-lg text-warm-gray">
              Chaque soirée est construite selon vos envies. Voici les 8 types de prestataires que Sax&DJ peut coordonner pour vous.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {inclusions.map((item, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-5 rounded-2xl border border-blue-100 hover:border-blue-300 hover:shadow-md transition-all duration-300 group"
              >
                <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-4 rounded-xl mb-3 group-hover:shadow-lg transition-all duration-300">
                  <item.icon className="h-7 w-7 text-white" />
                </div>
                <span className="text-sm font-semibold text-charcoal">{item.label}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-warm-gray text-sm mt-8 italic">
            Et bien d'autres selon votre projet, tout est possible sur-mesure.
          </p>
        </div>
      </section>

      {/* ── COMMENT ÇA MARCHE ── */}
      <section className="py-20 bg-gradient-to-br from-ivory/30 to-pearl/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Comment ça marche ?
            </h2>
            <p className="text-lg text-warm-gray max-w-2xl mx-auto">
              3 étapes simples pour passer de l'idée à la soirée parfaite.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((item, index) => (
              <div key={index} className="text-center group relative">
                <div className="relative mb-6 flex justify-center">
                  <div className="w-20 h-20 bg-gradient-to-r from-blue-900 to-blue-700 rounded-full flex flex-col items-center justify-center mx-auto group-hover:shadow-lg transition-all duration-300">
                    <span className="text-xs font-bold text-white/70 uppercase tracking-wide">Étape</span>
                    <span className="text-2xl font-bold text-white leading-none">{index + 1}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-10 left-[calc(50%+40px)] w-[calc(100%-80px)] h-0.5 bg-gradient-to-r from-blue-300 to-blue-500 -z-10" />
                  )}
                </div>
                <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-2 rounded-lg w-fit mx-auto mb-3">
                  <item.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-charcoal mb-3 group-hover:text-blue-900 transition-colors">
                  {item.title}
                </h3>
                <p className="text-warm-gray leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Questions fréquentes
            </h2>
            <p className="text-lg text-warm-gray">
              Tout ce que vous devez savoir avant de nous contacter.
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-gradient-to-br from-ivory/30 to-pearl/30 rounded-2xl border border-blue-100 overflow-hidden"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none font-semibold text-charcoal hover:text-blue-900 transition-colors">
                  <span>{faq.question}</span>
                  <ChevronDown className="h-5 w-5 text-blue-900 flex-shrink-0 ml-4 transition-transform duration-200 group-open:rotate-180" />
                </summary>
                <p className="px-6 pb-6 text-warm-gray leading-relaxed text-sm">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Parlons de votre prochaine soirée
          </h2>
          <p className="text-xl text-white/85 mb-3 leading-relaxed">
            Zone couverte : Rambouillet · Chartres · Yvelines (78) · Eure-et-Loir (28) · Île-de-France Sud
          </p>
          <p className="text-white/70 mb-10">
            Devis sur-mesure · Aucun engagement
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              asChild
              className="bg-gold hover:bg-yellow-500 text-blue-900 font-bold text-lg px-8 py-4 hover:shadow-xl transition-all duration-300 border-0"
            >
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                Prendre un rendez-vous
              </a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-4 transition-all duration-300"
            >
              <Link to="/contact">
                Envoyer une demande écrite
              </Link>
            </Button>
          </div>
        </div>
      </section>

    </div>
  );
};
